 import { Helmet } from 'react-helmet-async';
 import { APP_CONFIG } from '@/config/app';
 import { SEO_CONFIG } from '@/config/seo';
 import { LandingHeader } from '@/components/landing/LandingHeader';
 import { LandingFooter } from '@/components/landing/LandingFooter';
 import {
   Breadcrumb,
   BreadcrumbItem,
   BreadcrumbLink,
   BreadcrumbList,
   BreadcrumbPage,
   BreadcrumbSeparator,
 } from '@/components/ui/breadcrumb';
 import { Link } from 'react-router-dom';
 import { Container } from '@/components/g5';
 
 interface PublicPageLayoutProps {
   title: string;
   description: string;
   children: React.ReactNode;
   breadcrumbLabel?: string;
   canonicalPath?: string;
   ogImage?: string;
   jsonLd?: Record<string, unknown>;
 }
 
 export function PublicPageLayout({
   title,
   description,
   children,
   breadcrumbLabel,
   canonicalPath,
   ogImage,
   jsonLd,
 }: PublicPageLayoutProps) {
   const fullTitle = `${title} | ${APP_CONFIG.name}`;
   const canonicalUrl = canonicalPath ? `${SEO_CONFIG.baseUrl}${canonicalPath}` : undefined;
   const image = ogImage || SEO_CONFIG.defaultOgImage;

   // JSON-LD Breadcrumb schema
   const breadcrumbSchema = breadcrumbLabel
     ? {
         '@context': 'https://schema.org',
         '@type': 'BreadcrumbList',
         itemListElement: [
           {
             '@type': 'ListItem',
             position: 1,
             name: 'Home',
             item: SEO_CONFIG.baseUrl,
           },
           {
             '@type': 'ListItem',
             position: 2,
             name: breadcrumbLabel,
             item: canonicalUrl,
           },
         ],
       }
     : null;

   return (
     <>
       <Helmet defer={false}>
         <title>{fullTitle}</title>
         <meta name="description" content={description} />
         <meta name="robots" content="index, follow" />
         {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
         
         {/* Open Graph */}
         <meta property="og:title" content={fullTitle} />
         <meta property="og:description" content={description} />
         <meta property="og:type" content="website" />
         {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
         <meta property="og:site_name" content={SEO_CONFIG.siteName} />
         <meta property="og:locale" content={SEO_CONFIG.locale} />
         <meta property="og:image" content={image} />
         
         {/* Twitter */}
         <meta name="twitter:card" content="summary_large_image" />
         <meta name="twitter:title" content={fullTitle} />
         <meta name="twitter:description" content={description} />
         <meta name="twitter:image" content={image} />
         
         {/* JSON-LD Breadcrumb */}
         {breadcrumbSchema && (
           <script type="application/ld+json">
             {JSON.stringify(breadcrumbSchema)}
           </script>
         )}
         {/* Additional JSON-LD */}
         {jsonLd && (
           <script type="application/ld+json">
             {JSON.stringify(jsonLd)}
           </script>
         )}
       </Helmet>

       {/* `.ashlar-public` provides G5 tokens for the shared header/footer + page body */}
       <div className="ashlar-public min-h-screen flex flex-col">
         <LandingHeader />
         
         <main className="flex-1">
           {/* Breadcrumb navigation */}
           {breadcrumbLabel && (
             <div style={{ borderBottom: '1px solid var(--g5-line)' }}>
               <Container className="py-3">
                 <Breadcrumb>
                   <BreadcrumbList>
                     <BreadcrumbItem>
                       <BreadcrumbLink asChild>
                         <Link to="/">Home</Link>
                       </BreadcrumbLink>
                     </BreadcrumbItem>
                     <BreadcrumbSeparator />
                     <BreadcrumbItem>
                       <BreadcrumbPage>{breadcrumbLabel}</BreadcrumbPage>
                     </BreadcrumbItem>
                   </BreadcrumbList>
                 </Breadcrumb>
               </Container>
             </div>
           )}
           
           {children}
         </main>
         
         <LandingFooter />
       </div>
     </>
   );
 }
